import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV } from "@/data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-steel/20 bg-aero-surface/90 shadow-sm backdrop-blur-xl"
          : "border-b border-steel/10 bg-aero-surface/70 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-5 py-4 sm:px-8">
        <Logo size="md" />

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `relative font-body text-sm tracking-wide transition-colors duration-300 hover:text-saffron ${
                  isActive ? "text-saffron" : "text-steel"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative inline-block py-1">
                  {item.label}
                  <span
                    className={`absolute -bottom-0.5 left-0 h-px bg-saffron transition-all duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <button
            onClick={() => navigate("/about")}
            data-testid="header-our-story-btn"
            className="group flex items-center gap-2 rounded-full bg-saffron px-5 py-2.5 font-heading text-sm font-semibold text-white transition-all duration-300 hover:bg-saffron-soft"
          >
            Our Story
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button
          className="grid h-11 w-11 place-items-center rounded-full border border-steel/30 text-ink lg:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-steel/20 bg-aero-surface/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col px-5 py-6">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${item.to.replace("/", "")}`}
                    className={({ isActive }) =>
                      `block border-b border-steel/10 py-4 font-heading text-2xl font-semibold ${
                        isActive ? "text-saffron" : "text-ink"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-6 flex flex-col gap-3">
                <button
                  onClick={() => {
                    setOpen(false);
                    navigate("/about");
                  }}
                  data-testid="mobile-our-story-btn"
                  className="flex items-center justify-center gap-2 rounded-full bg-saffron px-5 py-3 font-heading font-semibold text-white"
                >
                  Our Story <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
